import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Badge, Spinner, Alert, Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import api, {
  authService,
  mapPvpParticipantToQuizAttemptResponse,
  pvpService,
  quizService,
  type LoginStreakDayUi,
  type LoginStreakHome,
  type PvPParticipantDto,
  type PvPRoom,
  type PvPRoomResultDto,
  type QuizAttemptResponse,
  type UnlockedAchievementPayload,
  type UnlockedCharacterPayload,
  type McPvpPlayStart,
  type StopPlayStart,
  type TileMatchPlayStart,
  type WitcPlayStart,
  type WitcFinishAudit,
} from '../services/api';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import TileMatchQuizPlayer, { type TileMatchPvpFinishPayload } from './TileMatchQuizPlayer';
import StopQuizPlayer, { type StopPvpFinishPayload } from './StopQuizPlayer';
import McQuizPlayer, {
  mapPvpMcQuestionsToApiQuestions,
  type McPvpFinishPayload,
} from './McQuizPlayer';
import WitcQuizPlayer from './WitcQuizPlayer';

import './shared/QuizStyles.css';
import PvpQuizResultsModalContent, {
  type PvpQuizResultsMapped,
} from './PvpQuizResultsModalContent';
import AsyncHostIndividualResultsModal from './AsyncHostIndividualResultsModal';
import { secondsToTimeSpanString } from '../utils/pvpAsync';
import { HOME_PVP_ARENA_PATH } from '../navigationPaths';
import { markPvpQuizEntryFromLobby, isPvpQuizSessionValidForRoom } from '../utils/pvpQuizEntry';
import { getQuizterSiteUrl } from '../utils/shareAchievementImage';
import { getQuizFullDisplayCaption } from '../utils/quizDisplayTitle';
import {
  maxPointsForPlayDifficultyBand,
  normalizePlayDifficultyLabel,
} from '../utils/quizPlayBandScoring';
import { isPwaStandalone } from '../utils/pwaStandalone';
import { playQuizSfxReward } from '../audio/quizSfx';
import QuizFinishCelebrationModal from './QuizFinishCelebrationModal';
import LevelUpCelebrationModal from './LevelUpCelebrationModal';
import { useQuizFinishLevelUp } from '../hooks/useQuizFinishLevelUp';
import type { QuizLevelUpDetectInput } from '../utils/quizLevelUpCelebration';
import GeniusQuizPlayer, { type GeniusPvpFinishPayload } from './GeniusQuizPlayer';
import WordConnectQuizPlayer, { type WordConnectPvpFinishPayload } from './WordConnectQuizPlayer';
import type { GeniusPlayStart, WordConnectPlayStart, Quiz } from '../services/api';
import { parseGeniusPlayStart } from '../utils/geniusQuizFormat';
import { GENIUS_TOTAL_ROUNDS } from '../utils/geniusPlayScoring';
import './QuizPlayer.css';

function parsePvpUnlockedAchievement(raw: unknown): UnlockedAchievementPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = o.id != null ? String(o.id) : '';
  if (!id) return null;
  const name = typeof o.name === 'string' ? o.name : '';
  const rarity = typeof o.rarity === 'string' ? o.rarity : '';
  const points =
    typeof o.points === 'number' && Number.isFinite(o.points) ? Math.floor(o.points) : 0;
  return {
    id,
    name,
    description: typeof o.description === 'string' ? o.description : null,
    rarity,
    soundKey: typeof o.soundKey === 'string' ? o.soundKey : null,
    iconAssetKey: typeof o.iconAssetKey === 'string' ? o.iconAssetKey : null,
    points,
    unlockedAt: typeof o.unlockedAt === 'string' ? o.unlockedAt : undefined,
  };
}

function isUuidSegment(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    s.trim()
  );
}

/** Canonical Arena async format key — each case in `loadRoomAndQuiz` must have a dedicated play loader. */
function normalizePvpArenaFormat(raw: string | null | undefined): string {
  return String(raw ?? '')
    .trim()
    .toLowerCase();
}

interface Question {
  id: string;
  text: string;
  textEn?: string;
  imageAssetKey?: string;
  options: string[];
  /** Parallel to `options` — English labels for multiple-choice (MC). */
  optionTextEns?: string[];
  /** Parallel to `options` — MC answer image basenames when quiz uses image answers. */
  optionImageAssetKeys?: string[];
  correctAnswer: number;
  explanation?: string;
  /** Display order of answer ids (matches options); required to persist PvP layout for the opponent. */
  answerIds?: string[];
  /** Witc Medium: underscore skeletons only. */
  witcMediumSkeletonPt?: string | null;
  witcMediumSkeletonEn?: string | null;
}

interface QuizData {
  id: string;
  title: string;
  titleEn?: string | null;
  theme?: string | null;
  themeEn?: string | null;
  format?: string;
  witcImageAssetsBasePath?: string | null;
  mcImageAssetsBasePath?: string | null;
  mcUseImageAnswers?: boolean;
  mcAnswerImageAssetsBasePath?: string | null;
  geniusImageAssetsBasePath?: string | null;
  geniusSoundAssetsBasePath?: string | null;
  difficulty: string;
  questions: Question[];
  scorePerCorrectAnswer: number;
  baseScore: number;
}

/** Lobby row: API gives completedAt for finished players; host is always done before opponent async quiz. */
interface PvpRoomParticipantRow {
  userId: string;
  nickname: string;
  isHost: boolean;
  completedAt: string | null;
}

function isAsyncArenaParticipantFinished(
  p: PvpRoomParticipantRow,
  asyncFlow: 'host' | 'opponent' | null
): boolean {
  if (p.completedAt) return true;
  if (asyncFlow === 'opponent' && p.isHost) return true;
  return false;
}

/**
 * Async Arena (opponent turn): some loads omit a participant row or send wrong `isHost`.
 * Derive host from `room.hostId`, append missing host/opponent so counts match production (e.g. 1 / 2).
 */
function normalizeAsyncArenaParticipantRowsForDisplay(
  asyncFlow: 'host' | 'opponent' | null,
  room: PvPRoom | null,
  rows: PvpRoomParticipantRow[]
): PvpRoomParticipantRow[] {
  if (asyncFlow !== 'opponent' || !room) return rows;
  const hostIdLc = String(room.hostId ?? '').trim().toLowerCase();
  if (!hostIdLc) return rows;

  const oppRaw = room.opponentUserId ?? null;
  const oppLc = oppRaw ? String(oppRaw).trim().toLowerCase() : '';

  const pickNick = (uidLc: string, nick: string) => {
    if (uidLc === hostIdLc) {
      const h = (room.hostNickname || room.hostName || '').trim();
      if (h) return h;
    }
    const n = (nick || '').trim();
    return n && n !== '—' ? n : '—';
  };

  const merged = new Map<string, PvpRoomParticipantRow>();

  for (const p of rows) {
    const uidLc = String(p.userId ?? '').trim().toLowerCase();
    if (!uidLc) continue;
    const isHostPeer = uidLc === hostIdLc;
    const next: PvpRoomParticipantRow = {
      userId: String(p.userId ?? '').trim() || p.userId,
      nickname: pickNick(uidLc, p.nickname),
      isHost: isHostPeer,
      completedAt: p.completedAt ?? null,
    };
    const prev = merged.get(uidLc);
    if (!prev) {
      merged.set(uidLc, next);
    } else {
      const prevNick = (prev.nickname || '').trim();
      const nextNick = (next.nickname || '').trim();
      merged.set(uidLc, {
        userId: prev.userId,
        nickname: prevNick && prevNick !== '—' ? prevNick : nextNick || prevNick || '—',
        isHost: isHostPeer,
        completedAt: next.completedAt ?? prev.completedAt ?? null,
      });
    }
  }

  const order: string[] = [];
  for (const p of rows) {
    const uidLc = String(p.userId ?? '').trim().toLowerCase();
    if (uidLc && !order.includes(uidLc)) order.push(uidLc);
  }

  const result: PvpRoomParticipantRow[] = order
    .map((id) => merged.get(id))
    .filter((x): x is PvpRoomParticipantRow => x != null);

  if (!merged.has(hostIdLc)) {
    const hostNick = (room.hostNickname || room.hostName || '').trim() || '—';
    result.push({
      userId: room.hostId,
      nickname: hostNick,
      isHost: true,
      completedAt: null,
    });
    merged.set(hostIdLc, result[result.length - 1]);
  }

  if (oppLc && !merged.has(oppLc)) {
    result.push({
      userId: String(oppRaw),
      nickname: '—',
      isHost: false,
      completedAt: null,
    });
  }

  return result;
}

function mapPvPRoomResultToQuizResults(dto: PvPRoomResultDto) {
  const parseCt = (ct: string | number): number => {
    if (typeof ct === 'number' && Number.isFinite(ct)) return Math.floor(ct);
    const parts = String(ct).split(':');
    if (parts.length === 3) {
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const s = parseInt(parts[2], 10);
      if (!Number.isFinite(h + m + s)) return 0;
      return h * 3600 + m * 60 + s;
    }
    return 0;
  };
  const mapped: PvpQuizResultsMapped = {
    roomName: dto.roomName,
    quizTitle: dto.quizTitle,
    quizTitleEn: dto.quizTitleEn,
    quizTheme: dto.quizTheme,
    quizThemeEn: dto.quizThemeEn,
    quizFormat: dto.quizFormat,
    endedAt: dto.endedAt,
    results: dto.results.map((r) => ({
      userId: r.userId,
      nickname: r.nickname ?? '',
      rank: r.rank,
      isHost: r.isHost,
      score: r.score,
      correctAnswers: r.correctAnswers,
      totalQuestions: r.totalQuestions,
      completionTime: parseCt(r.completionTime as string | number),
      earnedXP: r.earnedXP,
      newLevel: r.newLevel ?? 0,
      winnerBonusXp: r.winnerBonusXp ?? 0,
      isVip: r.isVip === true,
      baseXp: r.baseXp ?? 0,
      bonusXpPerCorrectAnswer: r.bonusXpPerCorrectAnswer ?? 0,
      totalBonusXp: r.totalBonusXp ?? 0,
      soloVipXpBonusAmount: r.soloVipXpBonusAmount ?? 0,
      pvpArenaExtraXp: r.pvpArenaExtraXp ?? 0,
      pvpAppXpBonusAmount: r.pvpAppXpBonusAmount ?? 0,
      pvpAppXpBonusPercent: r.pvpAppXpBonusPercent ?? null,
      speedBonusXp: r.speedBonusXp,
      soloXpPotionBonusAmount: r.soloXpPotionBonusAmount ?? 0,
      soloXpPotionBonusPercent: r.soloXpPotionBonusPercent ?? null,
    })),
  };
  return {
    ...mapped,
    unlockedAchievementsByUser: dto.unlockedAchievementsByUser,
    celebrationByUser: dto.celebrationByUser,
  };
}

function parsePvpUnlockedCharacter(raw: unknown): UnlockedCharacterPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const unlockableQuestionId = o.unlockableQuestionId != null ? String(o.unlockableQuestionId) : '';
  if (!unlockableQuestionId) return null;
  return {
    unlockableQuestionId,
    imageAssetKey: typeof o.imageAssetKey === 'string' ? o.imageAssetKey : null,
    imageAssetsBasePath: typeof o.imageAssetsBasePath === 'string' ? o.imageAssetsBasePath : null,
  };
}

function parseLoginStreakHomeFromPvp(raw: unknown): LoginStreakHome | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const currentStreak = typeof o.currentStreak === 'number' ? o.currentStreak : null;
  if (currentStreak == null || !Number.isFinite(currentStreak)) return null;
  const weekRaw = o.weekDays;
  const weekDays = Array.isArray(weekRaw)
    ? weekRaw
        .map((d) => {
          if (!d || typeof d !== 'object') return null;
          const wd = d as Record<string, unknown>;
          if (typeof wd.dayOfWeek !== 'number') return null;
          return {
            dayOfWeek: wd.dayOfWeek,
            completed: wd.completed === true,
            isToday: wd.isToday === true,
          };
        })
        .filter((x): x is LoginStreakDayUi => x !== null)
    : [];
  return { currentStreak, weekDays };
}

function pickSoloStyleCelebrationFromPvpMapped(
  mapped: ReturnType<typeof mapPvPRoomResultToQuizResults>,
  myIdLower: string
): {
  achievements: UnlockedAchievementPayload[];
  characters: UnlockedCharacterPayload[];
  streak: LoginStreakHome | null;
} {
  const celebration = mapped.celebrationByUser;
  const row = Array.isArray(celebration)
    ? celebration.find((c) => String(c.userId ?? '').toLowerCase() === myIdLower)
    : undefined;
  if (row) {
    const achievements = (Array.isArray(row.unlockedAchievements) ? row.unlockedAchievements : [])
      .map(parsePvpUnlockedAchievement)
      .filter((x): x is UnlockedAchievementPayload => x !== null);
    const characters = (Array.isArray(row.unlockedCharacters) ? row.unlockedCharacters : [])
      .map(parsePvpUnlockedCharacter)
      .filter((x): x is UnlockedCharacterPayload => x !== null);
    const streak = parseLoginStreakHomeFromPvp(row.loginStreakUpdated ?? null);
    return { achievements, characters, streak };
  }

  const byUser = mapped.unlockedAchievementsByUser as
    | { userId?: string; unlockedAchievements?: unknown[] }[]
    | undefined;
  const entry = Array.isArray(byUser)
    ? byUser.find((u) => String(u.userId ?? '').toLowerCase() === myIdLower)
    : undefined;
  const rawList =
    entry && Array.isArray(entry.unlockedAchievements) ? entry.unlockedAchievements : [];
  const achievements = rawList
    .map(parsePvpUnlockedAchievement)
    .filter((x): x is UnlockedAchievementPayload => x !== null);
  return { achievements, characters: [], streak: null };
}

type PvpPostMatchFlowActions = {
  prepareFromQuizResponse: (response: QuizLevelUpDetectInput) => boolean;
  tryOpenLevelUpCelebration: () => boolean;
  setCelebrationPlayerNick: (nick: string) => void;
  setCelebrationAchievements: (items: UnlockedAchievementPayload[]) => void;
  setCelebrationCharacters: (items: UnlockedCharacterPayload[]) => void;
  setCelebrationLoginStreak: (streak: LoginStreakHome | null) => void;
  setShowCelebration: (show: boolean) => void;
  setShowResults: (show: boolean) => void;
};

async function openPvpPostMatchCelebrationOrLevelUp(
  mapped: ReturnType<typeof mapPvPRoomResultToQuizResults>,
  actions: PvpPostMatchFlowActions
): Promise<void> {
  try {
    const me = await authService.getProfile();
    const myId = String(me.id ?? '').toLowerCase();
    const { achievements, characters, streak } = pickSoloStyleCelebrationFromPvpMapped(mapped, myId);
    const myResult = (mapped.results ?? []).find(
      (r) => String(r.userId ?? '').toLowerCase() === myId
    );
    const hasLevelUp = actions.prepareFromQuizResponse({
      newLevel: myResult?.newLevel,
      earnedXP: myResult?.earnedXP,
    });
    const hasCelebration =
      achievements.length > 0
      || characters.length > 0
      || (streak != null && typeof streak.currentStreak === 'number');
    if (hasCelebration) {
      actions.setCelebrationPlayerNick((me.nickname || me.name || '').trim());
      actions.setCelebrationAchievements(achievements);
      actions.setCelebrationCharacters(characters);
      actions.setCelebrationLoginStreak(streak);
      actions.setShowCelebration(true);
      return;
    }
    if (hasLevelUp && actions.tryOpenLevelUpCelebration()) {
      return;
    }
  } catch {
    /* fall through to results */
  }
  actions.setShowResults(true);
}

const PvPQuiz: React.FC = () => {
  const { t, i18n } = useSafeTranslation();
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [quiz, setQuiz] = useState<QuizData | null>(null);
  const [score, setScore] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  /** Everyone in the room when the quiz view loaded. */
  const [roomParticipants, setRoomParticipants] = useState<PvpRoomParticipantRow[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [quizResults, setQuizResults] = useState<any>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationAchievements, setCelebrationAchievements] = useState<UnlockedAchievementPayload[]>([]);
  const [celebrationCharacters, setCelebrationCharacters] = useState<UnlockedCharacterPayload[]>([]);
  const [celebrationLoginStreak, setCelebrationLoginStreak] = useState<LoginStreakHome | null>(null);
  const [celebrationPlayerNick, setCelebrationPlayerNick] = useState('');
  const levelUpCelebration = useQuizFinishLevelUp();
  const [showQuitModal, setShowQuitModal] = useState(false);
  /** host/opponent: Arena PvP room (draft / awaiting opponent). */
  const [asyncFlow, setAsyncFlow] = useState<'host' | 'opponent' | null>(null);
  const [roomInfo, setRoomInfo] = useState<PvPRoom | null>(null);
  const [showAsyncHostModal, setShowAsyncHostModal] = useState(false);
  const [asyncHostEarnedXp, setAsyncHostEarnedXp] = useState(0);
  /** Detalhamento de XP (base, velocidade, poção, arena, app…) para o modal async host. */
  const [asyncHostQuizResponse, setAsyncHostQuizResponse] = useState<QuizAttemptResponse | null>(null);
  /** Duração da rodada do host (modal de resultados, igual ao solo). */
  const [asyncHostDurationSeconds, setAsyncHostDurationSeconds] = useState<number | null>(null);
  /** Host completou com excesso de skips: sala cancelada, não publicada. */
  const [asyncHostPublishBlockedBySkips, setAsyncHostPublishBlockedBySkips] = useState(false);
  /** Oponente: excesso de skips — partida não concluída; sala volta ao lobby. */
  const [asyncOpponentBlockedBySkips, setAsyncOpponentBlockedBySkips] = useState(false);
  /** Async room already Closed: show final results without loading questions. */
  const [asyncClosedResultsOnly, setAsyncClosedResultsOnly] = useState(false);
  const [geniusStart, setGeniusStart] = useState<GeniusPlayStart | null>(null);
  const [wordConnectStart, setWordConnectStart] = useState<WordConnectPlayStart | null>(null);
  const [tileMatchStart, setTileMatchStart] = useState<TileMatchPlayStart | null>(null);
  const [witcStart, setWitcStart] = useState<WitcPlayStart | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const pvpResponseSecondsRef = useRef<number[]>([]);
  const pvpCorrectPerQuestionRef = useRef<boolean[]>([]);

  const [mcEngineBootstrapCipher, setMcEngineBootstrapCipher] = useState<string | null>(null);
  const [mcPlaySessionId, setMcPlaySessionId] = useState<string | null>(null);

  const [stopEngineBootstrapCipher, setStopEngineBootstrapCipher] = useState<string | null>(null);
  const [stopPlaySessionId, setStopPlaySessionId] = useState<string | null>(null);

  const isStopQuiz = quiz?.format === 'Stop' || quiz?.format === 'stop';
  const isMcQuiz = quiz?.format === 'MultipleChoice' || quiz?.format === 'multiplechoice';

  const displayParticipantRows = useMemo(
    () => normalizeAsyncArenaParticipantRowsForDisplay(asyncFlow, roomInfo, roomParticipants),
    [asyncFlow, roomInfo, roomParticipants]
  );

  const pvpParticipantsInlineCard = useMemo(() => {
    if (asyncFlow === 'host' || asyncFlow == null) return null;
    const rowParticipants = displayParticipantRows;
    const totalTracked = rowParticipants.length;
    const finishedCount = rowParticipants.filter((p) =>
      isAsyncArenaParticipantFinished(p, asyncFlow)
    ).length;
    return (
      <Card className="question-card pvp-participants-inline-card mb-3">
        <Card.Header className="py-2 px-3">
          <h5 className="mb-0 h6">
            {t('pvp.participantsLabel', 'Participants')}
            {totalTracked > 0 ? (
              <span className="text-muted fw-normal ms-1">
                (
                {t('pvp.participantsLiveSummary', '{{finished}} / {{total}} finished')
                  .replace('{{finished}}', String(finishedCount))
                  .replace('{{total}}', String(totalTracked))}
                )
              </span>
            ) : null}
          </h5>
        </Card.Header>
        <Card.Body className="py-2 px-3">
          {rowParticipants.length === 0 ? (
            <div className="text-muted small mb-0">
              {t('pvp.waitingForParticipants', 'Waiting for participants...')}
            </div>
          ) : (
            <div
              className="pvp-participants-row d-flex flex-nowrap align-items-stretch gap-2"
              role="list"
            >
              {rowParticipants.map((rp) => {
                const done = isAsyncArenaParticipantFinished(rp, asyncFlow);
                return (
                  <div
                    key={rp.userId}
                    className="pvp-participant-chip d-inline-flex align-items-center gap-1 flex-shrink-0"
                    role="listitem"
                  >
                    <span className="pvp-participant-chip__name" title={rp.nickname}>
                      {rp.nickname}
                    </span>
                    <Badge
                      bg={done ? 'success' : 'secondary'}
                      pill
                      className="pvp-participant-chip__badge"
                    >
                      {done
                        ? t('pvp.participantBadgeFinished', 'Done')
                        : t('pvp.participantBadgePlaying', 'Answering')}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </Card.Body>
      </Card>
    );
  }, [asyncFlow, displayParticipantRows, t]);

  const isWitcQuiz = quiz?.format === 'Witc' || quiz?.format === 'witc';

  useEffect(() => {
    setAsyncHostPublishBlockedBySkips(false);
    setAsyncOpponentBlockedBySkips(false);
  }, [roomId]);

  useEffect(() => {
    if (roomId) {
      void loadRoomAndQuiz();
    }
  }, [roomId]);

  const pvpResultsRewardDoneRef = useRef(false);
  useEffect(() => {
    if (!showResults) {
      pvpResultsRewardDoneRef.current = false;
      return;
    }
    if (!quizResults || pvpResultsRewardDoneRef.current) return;
    pvpResultsRewardDoneRef.current = true;
    playQuizSfxReward();
  }, [showResults, quizResults]);

  const pvpAsyncHostRewardDoneRef = useRef(false);
  useEffect(() => {
    if (!showAsyncHostModal) {
      pvpAsyncHostRewardDoneRef.current = false;
      return;
    }
    if (pvpAsyncHostRewardDoneRef.current) return;
    pvpAsyncHostRewardDoneRef.current = true;
    playQuizSfxReward();
  }, [showAsyncHostModal]);

  const pvpPostMatchFlowActions = useMemo(
    (): PvpPostMatchFlowActions => ({
      prepareFromQuizResponse: levelUpCelebration.prepareFromQuizResponse,
      tryOpenLevelUpCelebration: levelUpCelebration.tryOpenLevelUpCelebration,
      setCelebrationPlayerNick,
      setCelebrationAchievements,
      setCelebrationCharacters,
      setCelebrationLoginStreak,
      setShowCelebration,
      setShowResults,
    }),
    [levelUpCelebration]
  );

  const handlePvpCelebrationFinished = () => {
    setShowCelebration(false);
    setCelebrationAchievements([]);
    setCelebrationCharacters([]);
    setCelebrationLoginStreak(null);
    setCelebrationPlayerNick('');
    if (levelUpCelebration.tryOpenLevelUpCelebration()) {
      return;
    }
    setShowResults(true);
  };

  const handlePvpLevelUpFinished = () => {
    levelUpCelebration.closeLevelUpCelebration();
    setShowResults(true);
  };

  const loadRoomAndQuiz = async () => {
    const segment = (roomId ?? '').trim();
    try {
      const fromPvpLobby = (location.state as { fromPvpLobby?: boolean } | null)?.fromPvpLobby === true;
      if (fromPvpLobby) {
        markPvpQuizEntryFromLobby(segment);
        navigate('.', { replace: true, state: null });
      }
      setLoading(true);
      setAsyncClosedResultsOnly(false);

      let room: PvPRoom;
      if (!isUuidSegment(segment)) {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
        if (!token) {
          navigate(`/?tab=pvp&pvpJoin=${encodeURIComponent(segment)}`, { replace: true });
          return;
        }
        room = await pvpService.getPvPRoomByRoomCode(segment);
        if (room.status === 'AwaitingOpponent' && !room.isUserHost && !room.isUserInRoom) {
          navigate(`/?tab=pvp&pvpJoin=${encodeURIComponent(segment)}`, { replace: true });
          return;
        }
      } else {
        room = await pvpService.getPvPRoom(segment);
      }

      /* Active async match: require lobby handoff (React state on first navigation) or same-tab session
       * (refresh). Closed rooms stay reachable for shared results links without that handoff. */
      if (room.status !== 'Closed') {
        const ok = fromPvpLobby || isPvpQuizSessionValidForRoom(segment);
        if (!ok) {
          navigate(HOME_PVP_ARENA_PATH, { replace: true });
          setLoading(false);
          return;
        }
      }

      const rid = room.id;
      setRoomInfo(room);

      if (room.status === 'Closed') {
        const result = await pvpService.getPvPRoomResult(rid);
        const mapped = mapPvPRoomResultToQuizResults(result);
        setQuizResults(mapped);
        setAsyncClosedResultsOnly(true);
        await openPvpPostMatchCelebrationOrLevelUp(mapped, pvpPostMatchFlowActions);
        setLoading(false);
        return;
      }
      if (room.status === 'Cancelled') {
        navigate(HOME_PVP_ARENA_PATH);
        return;
      }
      if (room.status === 'AwaitingOpponent' && room.isUserHost) {
        navigate(HOME_PVP_ARENA_PATH);
        return;
      }
      let flow: 'host' | 'opponent' | null = null;
      if (room.status === 'Draft' && room.isUserHost) {
        flow = 'host';
      } else if (room.status === 'AwaitingOpponent' && room.isUserInRoom && !room.isUserHost) {
        flow = 'opponent';
      } else {
        navigate(HOME_PVP_ARENA_PATH);
        return;
      }
      setAsyncFlow(flow);

      const rawParticipants = room.participants;
      if (Array.isArray(rawParticipants) && rawParticipants.length > 0) {
        setRoomParticipants(
          rawParticipants.map((p: PvPParticipantDto) => ({
            userId: String(p.userId ?? ''),
            nickname: (p.nickname || p.userName || '—').trim() || '—',
            isHost: !!p.isHost,
            completedAt: p.completedAt ?? null,
          }))
        );
      } else {
        setRoomParticipants([]);
      }

      const quizId = room.quizId;
      const quizResponse = await api.get(`/quiz/${quizId}`);
      const quizData = quizResponse.data;

      if (!quizData) {
        throw new Error('Quiz not found');
      }

      const fmt = String(quizData.format ?? '');
      const arenaFormat = normalizePvpArenaFormat(fmt);

      switch (arenaFormat) {
        case 'genius': {
        const geniusPlay = await pvpService.getAsyncPvPRoomGeniusPlay(rid);
        const parsedGenius = parseGeniusPlayStart(geniusPlay);
        if (!parsedGenius) throw new Error('Invalid Genius PvP session');

        setGeniusStart(parsedGenius);
        setQuiz({
          id: quizData.id,
          title: quizData.title,
          titleEn: quizData.titleEn,
          theme: quizData.theme,
          themeEn: quizData.themeEn,
          format: 'Genius',
          geniusImageAssetsBasePath: quizData.geniusImageAssetsBasePath,
          geniusSoundAssetsBasePath: quizData.geniusSoundAssetsBasePath,
          difficulty: 'Medium',
          questions: [],
          scorePerCorrectAnswer: 5,
          baseScore: 0,
        });
        pvpResponseSecondsRef.current = Array.from({ length: GENIUS_TOTAL_ROUNDS }, () => 0);
        pvpCorrectPerQuestionRef.current = Array.from({ length: GENIUS_TOTAL_ROUNDS }, () => false);
        startTimeRef.current = Date.now();
        setLoading(false);
        return;
        }

        case 'wordconnect': {
        const wcPlay = await pvpService.getAsyncPvPRoomWordConnectPlay(rid);
        setWordConnectStart(wcPlay);
        setQuiz({
          id: quizData.id,
          title: quizData.title,
          titleEn: quizData.titleEn,
          theme: quizData.theme,
          themeEn: quizData.themeEn,
          format: 'WordConnect',
          difficulty: 'Medium',
          questions: [] as any[],
          scorePerCorrectAnswer: wcPlay.pointsPerWord,
          baseScore: 0,
        });
        startTimeRef.current = Date.now();
        setLoading(false);
        return;
        }

        case 'multiplechoice': {
        const mcPlay: McPvpPlayStart = await pvpService.getAsyncPvPRoomMcPlay(rid);
        setMcPlaySessionId(mcPlay.mcPlaySessionId);
        setMcEngineBootstrapCipher(mcPlay.mcEngineBootstrapCipher);

        const playBandLabel = normalizePlayDifficultyLabel(mcPlay.playDifficulty);
        const maxPts = maxPointsForPlayDifficultyBand(playBandLabel);

        const transformedQuestions = mcPlay.questions.map((q: any) => ({
          id: q.id,
          text: q.text,
          textEn: q.textEn,
          imageAssetKey: q.imageAssetKey,
          options: q.answers.map((a: any) => a.text),
          optionTextEns: q.answers.map((a: any) => a.textEn ?? ''),
          optionImageAssetKeys: q.answers.map((a: any) => a.imageAssetKey ?? ''),
          correctAnswer: -1,
          explanation: null,
          answerIds: q.answers.map((a: any) => a.id),
        }));

        setQuiz({
          ...quizData,
          id: quizData.id,
          title: quizData.title,
          titleEn: quizData.titleEn,
          theme: quizData.theme,
          themeEn: quizData.themeEn,
          format: 'MultipleChoice',
          difficulty: playBandLabel,
          questions: transformedQuestions,
          scorePerCorrectAnswer: maxPts,
          baseScore: 0,
        });

        startTimeRef.current = Date.now();
        setLoading(false);
        return;
        }

        case 'tilematch': {
        const tileMatchPlay = await pvpService.getAsyncPvPRoomTileMatchPlay(rid);
        setTileMatchStart(tileMatchPlay);
        setQuiz({
          id: quizData.id,
          title: quizData.title,
          titleEn: quizData.titleEn,
          theme: quizData.theme,
          themeEn: quizData.themeEn,
          format: 'TileMatch',
          difficulty: 'Medium',
          questions: [] as any[],
          scorePerCorrectAnswer: 100,
          baseScore: 0,
        });
        startTimeRef.current = Date.now();
        setLoading(false);
        return;
        }

        case 'witc': {
        const witcPlay = await pvpService.getAsyncPvPRoomWitcPlay(rid);
        setWitcStart(witcPlay);

        const questionsData =
          flow === 'opponent'
            ? await pvpService.getAsyncPvPRoomQuizQuestions(rid)
            : await quizService.getQuizQuestions(quizId, {
                context: 'pvp',
                pvpRoomId: rid,
              });

        if (!questionsData || !Array.isArray(questionsData) || questionsData.length === 0) {
          throw new Error('Quiz has no questions');
        }

        const transformedQuestions = questionsData.map((q: any) => ({
          id: q.id,
          text: q.text,
          textEn: q.textEn,
          imageAssetKey: q.imageAssetKey,
          options: [] as string[],
          correctAnswer: -1,
          explanation: null,
          answerIds: [] as string[],
          witcMediumSkeletonPt: q.witcMediumSkeletonPt ?? null,
          witcMediumSkeletonEn: q.witcMediumSkeletonEn ?? null,
        }));

        const playBandLabel = normalizePlayDifficultyLabel(room.quizDifficulty ?? quizData.difficulty);
        const maxPts = maxPointsForPlayDifficultyBand(playBandLabel);

        setQuiz({
          ...quizData,
          id: quizData.id,
          title: quizData.title,
          titleEn: quizData.titleEn,
          theme: quizData.theme,
          themeEn: quizData.themeEn,
          format: 'Witc',
          difficulty: playBandLabel,
          questions: transformedQuestions,
          scorePerCorrectAnswer: maxPts,
          witcImageAssetsBasePath:
            witcPlay.imageAssetsBasePath?.trim() || quizData.witcImageAssetsBasePath || '',
          baseScore: 0,
        });

        startTimeRef.current = Date.now();
        setLoading(false);
        return;
        }

        case 'stop': {
        const stopPlay: StopPlayStart = await pvpService.getAsyncPvPRoomStopPlay(rid);
        setStopPlaySessionId(stopPlay.playSessionId);
        setStopEngineBootstrapCipher(stopPlay.engineBootstrapCipher);

        const transformedQuestions = stopPlay.questions.map((q: any) => ({
          id: q.id,
          text: q.text,
          textEn: q.textEn,
          imageAssetKey: q.imageAssetKey,
          options: [] as string[],
          correctAnswer: -1,
          explanation: null,
          answerIds: [] as string[],
        }));

        setQuiz({
          ...quizData,
          id: quizData.id,
          title: quizData.title,
          titleEn: quizData.titleEn,
          theme: quizData.theme,
          themeEn: quizData.themeEn,
          format: 'Stop',
          difficulty: 'Medium',
          questions: transformedQuestions,
          scorePerCorrectAnswer: 10,
          baseScore: 0,
        });

        startTimeRef.current = Date.now();
        setLoading(false);
        return;
        }

        case 'mixed':
        default:
          throw new Error(`Unsupported PvP quiz format: ${fmt || arenaFormat || 'unknown'}`);
      }
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : '';
      if (msg.includes('Unsupported PvP quiz format')) {
        toast.error(
          t(
            'pvp.unsupportedFormatOrNotReady',
            'This quiz cannot be played in the Arena yet. Return to the lobby and try again.'
          )
        );
      } else if (msg) {
        toast.error(msg);
      }
      navigate(HOME_PVP_ARENA_PATH);
    } finally {
      setLoading(false);
    }
  };

  const handleQuitQuiz = () => {
    setShowQuitModal(true);
  };

  const confirmQuit = async () => {
    const rid = roomInfo?.id;
    if (rid) {
      try {
        if (asyncFlow === 'host') {
          await pvpService.abandonAsyncDraft(rid);
        } else if (asyncFlow === 'opponent') {
          await pvpService.abandonAsyncOpponentQuiz(rid);
        }
      } catch {
        /* ignore */
      }
    }
    navigate(HOME_PVP_ARENA_PATH);
  };

  const handleGeniusPvpComplete = async (payload: GeniusPvpFinishPayload) => {
    const rid = roomInfo?.id;
    if (!rid || !asyncFlow || quizCompleted) return;
    if (!payload.geniusFinishAudit) {
      toast.error('Genius finish audit is missing. Reload the quiz and try again.');
      return;
    }

    try {
      const completionTime = secondsToTimeSpanString(payload.durationSeconds);
      pvpResponseSecondsRef.current = payload.responseTimeSecondsPerQuestion;
      pvpCorrectPerQuestionRef.current = payload.correctPerQuestion;
      setScore(payload.score);
      setCorrectAnswers(payload.correctAnswers);

      const geniusResults = payload.geniusResults?.map((r) => ({
        pointsEarned: r.pointsEarned,
        finalAnswerText: r.finalAnswerText ?? null,
      })) ?? [];

      if (asyncFlow === 'host') {
        const updated = await pvpService.completeAsyncHostQuiz(
          rid,
          {
            score: payload.score,
            correctAnswers: payload.correctAnswers,
            totalQuestions: GENIUS_TOTAL_ROUNDS,
            completionTime,
            skippedQuestions: 0,
            questionIdsInOrder: payload.panelQuestionIds,
            answerIdsPerQuestion: payload.panelQuestionIds.map(() => []),
            geniusSequenceQuestionIds: payload.geniusSequenceQuestionIds,
            responseTimeSecondsPerQuestion: payload.responseTimeSecondsPerQuestion,
            correctPerQuestion: payload.correctPerQuestion,
            geniusFinishAudit: payload.geniusFinishAudit,
            geniusResults,
          },
          { pwaClient: isPwaStandalone() }
        );
        const blocked =
          updated.status === 'Cancelled'
          && updated.cancellationReason === 'HostQuizTooManySkips';
        setAsyncHostPublishBlockedBySkips(blocked);
        const hostP = updated.participants?.find((p) => p.isHost);
        const hostXp = blocked ? 0 : (hostP?.mainPhaseXpCredited ?? hostP?.earnedXP ?? 0);
        setAsyncHostEarnedXp(hostXp);
        setAsyncHostQuizResponse(
          blocked || !hostP ? null : mapPvpParticipantToQuizAttemptResponse(hostP)
        );
        setAsyncHostDurationSeconds(payload.durationSeconds);
        setRoomInfo(updated);
        setQuizCompleted(true);
        setShowAsyncHostModal(true);
        return;
      }

      const updated = await pvpService.completeAsyncOpponentQuiz(
        rid,
        {
          score: payload.score,
          correctAnswers: payload.correctAnswers,
          totalQuestions: GENIUS_TOTAL_ROUNDS,
          completionTime,
          skippedQuestions: 0,
          responseTimeSecondsPerQuestion: payload.responseTimeSecondsPerQuestion,
          correctPerQuestion: payload.correctPerQuestion,
          geniusFinishAudit: payload.geniusFinishAudit,
          geniusResults,
        },
        { pwaClient: isPwaStandalone() }
      );
      if (updated.opponentQuizAbortedDueToSkips) {
        setAsyncOpponentBlockedBySkips(true);
        setAsyncHostPublishBlockedBySkips(false);
        setAsyncHostQuizResponse(null);
        setRoomInfo(updated);
        setQuizCompleted(true);
        setShowAsyncHostModal(true);
        return;
      }
      const result = await pvpService.getPvPRoomResult(rid);
      const mapped = mapPvPRoomResultToQuizResults(result);
      setQuizResults(mapped);
      setQuizCompleted(true);

      await openPvpPostMatchCelebrationOrLevelUp(mapped, pvpPostMatchFlowActions);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? String((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
          : err instanceof Error
            ? err.message
            : 'Unknown error';
      toast.error(msg || t('pvp.submitFailed', 'Failed to submit results'));
    }
  };

  const handleWordConnectPvpComplete = async (payload: WordConnectPvpFinishPayload) => {
    const rid = roomInfo?.id;
    if (!rid || !asyncFlow || quizCompleted) return;
    if (!payload.wordConnectFinishAudit) {
      toast.error('Word Connect finish audit is missing. Reload the quiz and try again.');
      return;
    }

    try {
      const completionTime = secondsToTimeSpanString(payload.duration);
      setScore(payload.score);
      setCorrectAnswers(payload.correctAnswers);

      if (asyncFlow === 'host') {
        const updated = await pvpService.completeAsyncHostQuiz(
          rid,
          {
            score: payload.score,
            correctAnswers: payload.correctAnswers,
            totalQuestions: payload.totalQuestions,
            completionTime,
            skippedQuestions: 0,
            questionIdsInOrder: payload.questionIdsInOrder,
            answerIdsPerQuestion: payload.questionIdsInOrder.map(() => []),
            responseTimeSecondsPerQuestion: payload.responseTimeSecondsPerQuestion,
            correctPerQuestion: payload.correctPerQuestion,
            wordConnectFinishAudit: payload.wordConnectFinishAudit,
            wordConnectResults: payload.wordConnectResults,
          },
          { pwaClient: isPwaStandalone() }
        );
        const blocked =
          updated.status === 'Cancelled'
          && updated.cancellationReason === 'HostQuizTooManySkips';
        setAsyncHostPublishBlockedBySkips(blocked);
        const hostP = updated.participants?.find((p) => p.isHost);
        const hostXp = blocked ? 0 : (hostP?.mainPhaseXpCredited ?? hostP?.earnedXP ?? 0);
        setAsyncHostEarnedXp(hostXp);
        setAsyncHostQuizResponse(
          blocked || !hostP ? null : mapPvpParticipantToQuizAttemptResponse(hostP)
        );
        setAsyncHostDurationSeconds(payload.duration);
        setRoomInfo(updated);
        setQuizCompleted(true);
        setShowAsyncHostModal(true);
        return;
      }

      const updated = await pvpService.completeAsyncOpponentQuiz(
        rid,
        {
          score: payload.score,
          correctAnswers: payload.correctAnswers,
          totalQuestions: payload.totalQuestions,
          completionTime,
          skippedQuestions: 0,
          responseTimeSecondsPerQuestion: payload.responseTimeSecondsPerQuestion,
          correctPerQuestion: payload.correctPerQuestion,
          wordConnectFinishAudit: payload.wordConnectFinishAudit,
          wordConnectResults: payload.wordConnectResults,
        },
        { pwaClient: isPwaStandalone() }
      );
      if (updated.opponentQuizAbortedDueToSkips) {
        setAsyncOpponentBlockedBySkips(true);
        setAsyncHostPublishBlockedBySkips(false);
        setAsyncHostQuizResponse(null);
        setRoomInfo(updated);
        setQuizCompleted(true);
        setShowAsyncHostModal(true);
        return;
      }
      const result = await pvpService.getPvPRoomResult(rid);
      const mapped = mapPvPRoomResultToQuizResults(result);
      setQuizResults(mapped);
      setQuizCompleted(true);

      await openPvpPostMatchCelebrationOrLevelUp(mapped, pvpPostMatchFlowActions);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? String((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
          : err instanceof Error
            ? err.message
            : 'Unknown error';
      toast.error(msg || t('pvp.submitFailed', 'Failed to submit results'));
    }
  };

  const handleStopPvpComplete = async (payload: StopPvpFinishPayload) => {
    const rid = roomInfo?.id;
    if (!rid || !asyncFlow || quizCompleted) return;
    if (!payload.stopFinishAudit) {
      toast.error('Stop finish audit is missing. Reload the quiz and try again.');
      return;
    }

    try {
      const completionTime = secondsToTimeSpanString(payload.duration);
      setScore(payload.score);
      setCorrectAnswers(payload.correctAnswers);
      pvpResponseSecondsRef.current = payload.responseTimeSecondsPerQuestion;
      pvpCorrectPerQuestionRef.current = payload.correctPerQuestion;

      const stopResults = payload.stopFinishAudit.pointsPerQuestion.map((pts: number) => ({
        pointsEarned: pts,
        finalAnswerText: null,
      }));

      const skippedCount = payload.responseTimeSecondsPerQuestion.filter((t, idx) => 
        !payload.correctPerQuestion[idx] && t === 0
      ).length;

      if (asyncFlow === 'host') {
        const updated = await pvpService.completeAsyncHostQuiz(
          rid,
          {
            score: payload.score,
            correctAnswers: payload.correctAnswers,
            totalQuestions: payload.totalQuestions,
            completionTime,
            skippedQuestions: skippedCount,
            questionIdsInOrder: payload.questionIdsInOrder,
            answerIdsPerQuestion: payload.questionIdsInOrder.map(() => []),
            responseTimeSecondsPerQuestion: payload.responseTimeSecondsPerQuestion,
            correctPerQuestion: payload.correctPerQuestion,
            stopFinishAudit: payload.stopFinishAudit,
            stopResults,
          },
          { pwaClient: isPwaStandalone() }
        );
        const blocked =
          updated.status === 'Cancelled'
          && updated.cancellationReason === 'HostQuizTooManySkips';
        setAsyncHostPublishBlockedBySkips(blocked);
        const hostP = updated.participants?.find((p) => p.isHost);
        const hostXp = blocked ? 0 : (hostP?.mainPhaseXpCredited ?? hostP?.earnedXP ?? 0);
        setAsyncHostEarnedXp(hostXp);
        setAsyncHostQuizResponse(
          blocked || !hostP ? null : mapPvpParticipantToQuizAttemptResponse(hostP)
        );
        setAsyncHostDurationSeconds(payload.duration);
        setRoomInfo(updated);
        setQuizCompleted(true);
        setShowAsyncHostModal(true);
        return;
      }

      const updated = await pvpService.completeAsyncOpponentQuiz(
        rid,
        {
          score: payload.score,
          correctAnswers: payload.correctAnswers,
          totalQuestions: payload.totalQuestions,
          completionTime,
          skippedQuestions: skippedCount,
          responseTimeSecondsPerQuestion: payload.responseTimeSecondsPerQuestion,
          correctPerQuestion: payload.correctPerQuestion,
          stopFinishAudit: payload.stopFinishAudit,
          stopResults,
        },
        { pwaClient: isPwaStandalone() }
      );
      if (updated.opponentQuizAbortedDueToSkips) {
        setAsyncOpponentBlockedBySkips(true);
        setAsyncHostPublishBlockedBySkips(false);
        setAsyncHostQuizResponse(null);
        setRoomInfo(updated);
        setQuizCompleted(true);
        setShowAsyncHostModal(true);
        return;
      }
      const result = await pvpService.getPvPRoomResult(rid);
      const mapped = mapPvPRoomResultToQuizResults(result);
      setQuizResults(mapped);
      setQuizCompleted(true);

      await openPvpPostMatchCelebrationOrLevelUp(mapped, pvpPostMatchFlowActions);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? String((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
          : err instanceof Error
            ? err.message
            : 'Unknown error';
      toast.error(msg || t('pvp.submitFailed', 'Failed to submit results'));
    }
  };

  const handleMcPvpComplete = async (payload: McPvpFinishPayload) => {
    const rid = roomInfo?.id;
    if (!rid || !asyncFlow || quizCompleted) return;
    if (!payload.mcFinishAudit) {
      toast.error('Multiple Choice finish audit is missing. Reload the quiz and try again.');
      return;
    }

    try {
      const completionTime = secondsToTimeSpanString(payload.durationSeconds);
      setScore(payload.score);
      setCorrectAnswers(payload.correctAnswers);
      pvpResponseSecondsRef.current = payload.responseTimeSecondsPerQuestion;
      pvpCorrectPerQuestionRef.current = payload.correctPerQuestion;

      if (asyncFlow === 'host') {
        const updated = await pvpService.completeAsyncHostQuiz(
          rid,
          {
            score: payload.score,
            correctAnswers: payload.correctAnswers,
            totalQuestions: payload.totalQuestions,
            completionTime,
            skippedQuestions: payload.skippedQuestions,
            questionIdsInOrder: payload.questionIdsInOrder,
            answerIdsPerQuestion: payload.answerIdsPerQuestion,
            responseTimeSecondsPerQuestion: payload.responseTimeSecondsPerQuestion,
            correctPerQuestion: payload.correctPerQuestion,
            mcFinishAudit: payload.mcFinishAudit,
          },
          { pwaClient: isPwaStandalone() }
        );
        const blocked =
          updated.status === 'Cancelled'
          && updated.cancellationReason === 'HostQuizTooManySkips';
        setAsyncHostPublishBlockedBySkips(blocked);
        const hostP = updated.participants?.find((p) => p.isHost);
        const hostXp = blocked ? 0 : (hostP?.mainPhaseXpCredited ?? hostP?.earnedXP ?? 0);
        setAsyncHostEarnedXp(hostXp);
        setAsyncHostQuizResponse(
          blocked || !hostP ? null : mapPvpParticipantToQuizAttemptResponse(hostP)
        );
        setAsyncHostDurationSeconds(payload.durationSeconds);
        setRoomInfo(updated);
        setQuizCompleted(true);
        setShowAsyncHostModal(true);
        return;
      }

      const updated = await pvpService.completeAsyncOpponentQuiz(
        rid,
        {
          score: payload.score,
          correctAnswers: payload.correctAnswers,
          totalQuestions: payload.totalQuestions,
          completionTime,
          skippedQuestions: payload.skippedQuestions,
          responseTimeSecondsPerQuestion: payload.responseTimeSecondsPerQuestion,
          correctPerQuestion: payload.correctPerQuestion,
          mcFinishAudit: payload.mcFinishAudit,
        },
        { pwaClient: isPwaStandalone() }
      );
      if (updated.opponentQuizAbortedDueToSkips) {
        setAsyncOpponentBlockedBySkips(true);
        setAsyncHostPublishBlockedBySkips(false);
        setAsyncHostQuizResponse(null);
        setRoomInfo(updated);
        setQuizCompleted(true);
        setShowAsyncHostModal(true);
        return;
      }
      const result = await pvpService.getPvPRoomResult(rid);
      const mapped = mapPvPRoomResultToQuizResults(result);
      setQuizResults(mapped);
      setQuizCompleted(true);

      await openPvpPostMatchCelebrationOrLevelUp(mapped, pvpPostMatchFlowActions);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? String((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
          : err instanceof Error
            ? err.message
            : 'Unknown error';
      toast.error(msg || t('pvp.submitFailed', 'Failed to submit results'));
    }
  };

  const handleWitcPvpComplete = async (payload: {
    score: number;
    correctAnswers: number;
    totalQuestions: number;
    durationSeconds: number;
    skippedQuestions: number;
    questionIdsInOrder: string[];
    responseTimeSecondsPerQuestion: number[];
    correctPerQuestion: boolean[];
    witcFinishAudit?: WitcFinishAudit | null;
    witcResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }> | null;
  }) => {
    const rid = roomInfo?.id;
    if (!rid || !asyncFlow || quizCompleted) return;
    if (!payload.witcFinishAudit) {
      toast.error('WITC finish audit is missing. Reload the quiz and try again.');
      return;
    }

    try {
      const completionTime = secondsToTimeSpanString(payload.durationSeconds);
      setScore(payload.score);
      setCorrectAnswers(payload.correctAnswers);
      pvpResponseSecondsRef.current = payload.responseTimeSecondsPerQuestion;
      pvpCorrectPerQuestionRef.current = payload.correctPerQuestion;

      const witcResults = payload.witcResults?.map((r) => ({
        pointsEarned: r.pointsEarned,
        finalAnswerText: r.finalAnswerText ?? null,
      })) ?? [];

      if (asyncFlow === 'host') {
        const updated = await pvpService.completeAsyncHostQuiz(
          rid,
          {
            score: payload.score,
            correctAnswers: payload.correctAnswers,
            totalQuestions: payload.totalQuestions,
            completionTime,
            skippedQuestions: payload.skippedQuestions,
            questionIdsInOrder: payload.questionIdsInOrder,
            answerIdsPerQuestion: payload.questionIdsInOrder.map(() => []),
            responseTimeSecondsPerQuestion: payload.responseTimeSecondsPerQuestion,
            correctPerQuestion: payload.correctPerQuestion,
            witcFinishAudit: payload.witcFinishAudit,
            witcResults,
          },
          { pwaClient: isPwaStandalone() }
        );
        const blocked =
          updated.status === 'Cancelled'
          && updated.cancellationReason === 'HostQuizTooManySkips';
        setAsyncHostPublishBlockedBySkips(blocked);
        const hostP = updated.participants?.find((p) => p.isHost);
        const hostXp = blocked ? 0 : (hostP?.mainPhaseXpCredited ?? hostP?.earnedXP ?? 0);
        setAsyncHostEarnedXp(hostXp);
        setAsyncHostQuizResponse(
          blocked || !hostP ? null : mapPvpParticipantToQuizAttemptResponse(hostP)
        );
        setAsyncHostDurationSeconds(payload.durationSeconds);
        setRoomInfo(updated);
        setQuizCompleted(true);
        setShowAsyncHostModal(true);
        return;
      }

      const updated = await pvpService.completeAsyncOpponentQuiz(
        rid,
        {
          score: payload.score,
          correctAnswers: payload.correctAnswers,
          totalQuestions: payload.totalQuestions,
          completionTime,
          skippedQuestions: payload.skippedQuestions,
          responseTimeSecondsPerQuestion: payload.responseTimeSecondsPerQuestion,
          correctPerQuestion: payload.correctPerQuestion,
          witcFinishAudit: payload.witcFinishAudit,
          witcResults,
        },
        { pwaClient: isPwaStandalone() }
      );
      if (updated.opponentQuizAbortedDueToSkips) {
        setAsyncOpponentBlockedBySkips(true);
        setAsyncHostPublishBlockedBySkips(false);
        setAsyncHostQuizResponse(null);
        setRoomInfo(updated);
        setQuizCompleted(true);
        setShowAsyncHostModal(true);
        return;
      }
      const result = await pvpService.getPvPRoomResult(rid);
      const mapped = mapPvPRoomResultToQuizResults(result);
      setQuizResults(mapped);
      setQuizCompleted(true);

      await openPvpPostMatchCelebrationOrLevelUp(mapped, pvpPostMatchFlowActions);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? String((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
          : err instanceof Error
            ? err.message
            : 'Unknown error';
      toast.error(msg || t('pvp.submitFailed', 'Failed to submit results'));
    }
  };

  const handleTileMatchPvpComplete = async (payload: TileMatchPvpFinishPayload) => {
    const rid = roomInfo?.id;
    if (!rid || !asyncFlow || quizCompleted) return;
    if (!payload.tileMatchFinishAudit) {
      toast.error('Tile Match finish audit is missing. Reload the quiz and try again.');
      return;
    }

    try {
      const completionTime = secondsToTimeSpanString(payload.duration);
      setScore(payload.score);
      setCorrectAnswers(payload.correctAnswers);
      pvpResponseSecondsRef.current = payload.responseTimeSecondsPerQuestion;
      pvpCorrectPerQuestionRef.current = payload.correctPerQuestion;

      if (asyncFlow === 'host') {
        const updated = await pvpService.completeAsyncHostQuiz(
          rid,
          {
            score: payload.score,
            correctAnswers: payload.correctAnswers,
            totalQuestions: payload.totalQuestions,
            completionTime,
            skippedQuestions: 0,
            questionIdsInOrder: payload.questionIdsInOrder,
            answerIdsPerQuestion: payload.questionIdsInOrder.map(() => []),
            responseTimeSecondsPerQuestion: payload.responseTimeSecondsPerQuestion,
            correctPerQuestion: payload.correctPerQuestion,
            tileMatchFinishAudit: payload.tileMatchFinishAudit,
            tileMatchResults: payload.tileMatchResults,
          },
          { pwaClient: isPwaStandalone() }
        );
        const blocked =
          updated.status === 'Cancelled'
          && updated.cancellationReason === 'HostQuizTooManySkips';
        setAsyncHostPublishBlockedBySkips(blocked);
        const hostP = updated.participants?.find((p) => p.isHost);
        const hostXp = blocked ? 0 : (hostP?.mainPhaseXpCredited ?? hostP?.earnedXP ?? 0);
        setAsyncHostEarnedXp(hostXp);
        setAsyncHostQuizResponse(
          blocked || !hostP ? null : mapPvpParticipantToQuizAttemptResponse(hostP)
        );
        setAsyncHostDurationSeconds(payload.duration);
        setRoomInfo(updated);
        setQuizCompleted(true);
        setShowAsyncHostModal(true);
        return;
      }

      const updated = await pvpService.completeAsyncOpponentQuiz(
        rid,
        {
          score: payload.score,
          correctAnswers: payload.correctAnswers,
          totalQuestions: payload.totalQuestions,
          completionTime,
          skippedQuestions: 0,
          responseTimeSecondsPerQuestion: payload.responseTimeSecondsPerQuestion,
          correctPerQuestion: payload.correctPerQuestion,
          tileMatchFinishAudit: payload.tileMatchFinishAudit,
          tileMatchResults: payload.tileMatchResults,
        },
        { pwaClient: isPwaStandalone() }
      );
      if (updated.opponentQuizAbortedDueToSkips) {
        setAsyncOpponentBlockedBySkips(true);
        setAsyncHostPublishBlockedBySkips(false);
        setAsyncHostQuizResponse(null);
        setRoomInfo(updated);
        setQuizCompleted(true);
        setShowAsyncHostModal(true);
        return;
      }
      const result = await pvpService.getPvPRoomResult(rid);
      const mapped = mapPvPRoomResultToQuizResults(result);
      setQuizResults(mapped);
      setQuizCompleted(true);

      await openPvpPostMatchCelebrationOrLevelUp(mapped, pvpPostMatchFlowActions);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? String((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
          : err instanceof Error
            ? err.message
            : 'Unknown error';
      toast.error(msg || t('pvp.submitFailed', 'Failed to submit results'));
    }
  };

  if (loading) {
    return (
      <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
        <div className="quiz-finish-saving-overlay__inner">
          <Spinner animation="border" role="presentation" />
          <span className="text-white mt-2 small">{t('quiz.loadingQuiz', 'Loading game...')}</span>
        </div>
      </div>
    );
  }

  if (
    asyncClosedResultsOnly
    && quizResults
    && (showResults || showCelebration || levelUpCelebration.showLevelUp)
  ) {
    return (
      <>
        <QuizFinishCelebrationModal
          show={showCelebration}
          onFinished={handlePvpCelebrationFinished}
          achievements={celebrationAchievements}
          characters={celebrationCharacters}
          loginStreak={celebrationLoginStreak}
          playerNickname={celebrationPlayerNick}
        />
        <LevelUpCelebrationModal
          show={levelUpCelebration.showLevelUp}
          oldLevel={levelUpCelebration.levelUpOld}
          newLevel={levelUpCelebration.levelUpNew}
          onClose={handlePvpLevelUpFinished}
        />
        {showResults && (
        <Modal
          show={showResults}
          onHide={() => {
            /* Intentionally no-op: dismiss only via footer button */
          }}
          backdrop="static"
          keyboard={false}
          fullscreen="lg-down"
          size="lg"
          scrollable
          dialogClassName="pvp-quiz-results-modal-dialog"
        >
          <Modal.Header closeButton={false}>
            <Modal.Title>{t('pvp.pvpQuizResults', 'PvP Quiz Results')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <PvpQuizResultsModalContent
              data={{
                roomName: quizResults.roomName,
                quizTitle: quizResults.quizTitle,
                quizTitleEn: quizResults.quizTitleEn,
                quizTheme: quizResults.quizTheme,
                quizThemeEn: quizResults.quizThemeEn,
                quizFormat: quizResults.quizFormat,
                endedAt: quizResults.endedAt,
                results: quizResults.results,
              }}
            />
          </Modal.Body>
          <Modal.Footer className="justify-content-between flex-wrap gap-2">
            <Button variant="outline-secondary" onClick={() => navigate('/')}>
              {t('quiz.backToHome', 'Back to Home')}
            </Button>
            <Button variant="primary" onClick={() => navigate(HOME_PVP_ARENA_PATH)}>
              {t('pvp.asyncHostGoArena', 'Back to Arena')}
            </Button>
          </Modal.Footer>
        </Modal>
        )}
      </>
    );
  }

  if (!quiz) {
    return (
      <div className="quiz-error">
        <Alert variant="danger">
          {t('pvp.quizNotFound', 'Quiz not found')}
        </Alert>
      </div>
    );
  }

  const isGeniusQuiz = quiz.format === 'Genius' || quiz.format === 'genius';
  if (isGeniusQuiz && geniusStart && asyncFlow && roomInfo) {
    const quizForGenius: Quiz = {
      id: quiz.id,
      title: quiz.title,
      titleEn: quiz.titleEn ?? null,
      theme: quiz.theme ?? null,
      themeEn: quiz.themeEn ?? null,
      format: 'Genius',
      difficulty: 'Medium',
      questionCount: GENIUS_TOTAL_ROUNDS,
      geniusImageAssetsBasePath: quiz.geniusImageAssetsBasePath ?? null,
      geniusSoundAssetsBasePath: quiz.geniusSoundAssetsBasePath ?? null,
    } as Quiz;

    return (
      <>
        <div className="quiz-page">
          <div className="quiz-container">
            {pvpParticipantsInlineCard}
            <GeniusQuizPlayer
              quizId={quiz.id}
              quiz={quizForGenius}
              genius={geniusStart}
              startTime={new Date(startTimeRef.current)}
              onPlayAgain={() => navigate(HOME_PVP_ARENA_PATH)}
              suppressResultsModal
              embeddedInPvp
              onPvpQuitRequest={handleQuitQuiz}
              pvpAsync={{
                flow: asyncFlow,
                roomId: roomInfo.id,
                onComplete: handleGeniusPvpComplete,
              }}
            />
          </div>
        </div>
        <Modal show={showQuitModal} onHide={() => setShowQuitModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{t('pvp.quitPvPQuiz', 'Quit PvP Quiz?')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t(
              'pvp.quitPvPQuizConfirm',
              'Are you sure you want to quit this PvP quiz? You will be removed from the room and your progress will be lost.'
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowQuitModal(false)}>
              {t('pvp.continuePvPQuiz', 'Continue Quiz')}
            </Button>
            <Button variant="danger" onClick={() => void confirmQuit()}>
              {t('pvp.quitPvPQuizButton', 'Quit Quiz')}
            </Button>
          </Modal.Footer>
        </Modal>
        <AsyncHostIndividualResultsModal
          show={showAsyncHostModal}
          onHide={() => {
            setShowAsyncHostModal(false);
            setAsyncHostQuizResponse(null);
            navigate(HOME_PVP_ARENA_PATH);
          }}
          roomName={roomInfo.name ?? ''}
          quizTitle={getQuizFullDisplayCaption(quizForGenius, i18n.language)}
          score={score}
          correctAnswers={correctAnswers}
          totalQuestions={GENIUS_TOTAL_ROUNDS}
          earnedXpPreview={asyncHostEarnedXp}
          quizResponse={asyncHostQuizResponse}
          resultDurationSeconds={asyncHostDurationSeconds}
          publishBlockedDueToSkips={asyncHostPublishBlockedBySkips}
          opponentSkipsBlocked={asyncOpponentBlockedBySkips}
          inviteUrl={
            roomInfo.roomCode
            && !asyncHostPublishBlockedBySkips
            && !asyncOpponentBlockedBySkips
              ? `${getQuizterSiteUrl()}/pvp-quiz/${roomInfo.roomCode}`
              : undefined
          }
        />
        <QuizFinishCelebrationModal
          show={showCelebration}
          onFinished={handlePvpCelebrationFinished}
          achievements={celebrationAchievements}
          characters={celebrationCharacters}
          loginStreak={celebrationLoginStreak}
          playerNickname={celebrationPlayerNick}
        />
        <LevelUpCelebrationModal
          show={levelUpCelebration.showLevelUp}
          oldLevel={levelUpCelebration.levelUpOld}
          newLevel={levelUpCelebration.levelUpNew}
          onClose={handlePvpLevelUpFinished}
        />
        {showResults && quizResults && (
          <Modal show={showResults} onHide={() => setShowResults(false)} size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title>{t('pvp.results', 'Results')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <PvpQuizResultsModalContent data={quizResults} />
            </Modal.Body>
            <Modal.Footer className="justify-content-between flex-wrap gap-2">
              <Button variant="outline-secondary" onClick={() => navigate('/')}>
                {t('quiz.backToHome', 'Back to Home')}
              </Button>
              <Button variant="primary" onClick={() => navigate(HOME_PVP_ARENA_PATH)}>
                {t('pvp.asyncHostGoArena', 'Back to Arena')}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </>
    );
  }

  const isWordConnectQuiz = quiz.format === 'WordConnect' || quiz.format === 'wordconnect';
  if (isWordConnectQuiz && wordConnectStart && asyncFlow && roomInfo) {
    const quizForWordConnect: Quiz = {
      id: quiz.id,
      title: quiz.title,
      titleEn: quiz.titleEn ?? null,
      theme: quiz.theme ?? null,
      themeEn: quiz.themeEn ?? null,
      format: 'WordConnect',
      difficulty: 'Medium',
      questionCount: wordConnectStart.activeWordCount,
    } as Quiz;

    return (
      <>
        <div className="quiz-page">
          <div className="quiz-container">
            {pvpParticipantsInlineCard}
            <WordConnectQuizPlayer
              quizId={quiz.id}
              quiz={quizForWordConnect}
              wordConnect={wordConnectStart}
              startTime={new Date(startTimeRef.current)}
              playDifficulty={roomInfo.quizDifficulty}
              onPlayAgain={() => navigate(HOME_PVP_ARENA_PATH)}
              embeddedInPvp
              onPvpQuitRequest={handleQuitQuiz}
              pvpAsync={{
                flow: asyncFlow,
                roomId: roomInfo.id,
                onComplete: handleWordConnectPvpComplete,
              }}
            />
          </div>
        </div>
        <Modal show={showQuitModal} onHide={() => setShowQuitModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{t('pvp.quitPvPQuiz', 'Quit PvP Quiz?')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t(
              'pvp.quitPvPQuizConfirm',
              'Are you sure you want to quit this PvP quiz? You will be removed from the room and your progress will be lost.'
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowQuitModal(false)}>
              {t('pvp.continuePvPQuiz', 'Continue Quiz')}
            </Button>
            <Button variant="danger" onClick={() => void confirmQuit()}>
              {t('pvp.quitPvPQuizButton', 'Quit Quiz')}
            </Button>
          </Modal.Footer>
        </Modal>
        <AsyncHostIndividualResultsModal
          show={showAsyncHostModal}
          onHide={() => {
            setShowAsyncHostModal(false);
            setAsyncHostQuizResponse(null);
            navigate(HOME_PVP_ARENA_PATH);
          }}
          roomName={roomInfo.name ?? ''}
          quizTitle={getQuizFullDisplayCaption(quizForWordConnect, i18n.language)}
          score={score}
          correctAnswers={correctAnswers}
          totalQuestions={wordConnectStart.activeWordCount}
          earnedXpPreview={asyncHostEarnedXp}
          quizResponse={asyncHostQuizResponse}
          resultDurationSeconds={asyncHostDurationSeconds}
          publishBlockedDueToSkips={asyncHostPublishBlockedBySkips}
          opponentSkipsBlocked={asyncOpponentBlockedBySkips}
          inviteUrl={
            roomInfo.roomCode
            && !asyncHostPublishBlockedBySkips
            && !asyncOpponentBlockedBySkips
              ? `${getQuizterSiteUrl()}/pvp-quiz/${roomInfo.roomCode}`
              : undefined
          }
        />
        <QuizFinishCelebrationModal
          show={showCelebration}
          onFinished={handlePvpCelebrationFinished}
          achievements={celebrationAchievements}
          characters={celebrationCharacters}
          loginStreak={celebrationLoginStreak}
          playerNickname={celebrationPlayerNick}
        />
        <LevelUpCelebrationModal
          show={levelUpCelebration.showLevelUp}
          oldLevel={levelUpCelebration.levelUpOld}
          newLevel={levelUpCelebration.levelUpNew}
          onClose={handlePvpLevelUpFinished}
        />
        {showResults && quizResults && (
          <Modal show={showResults} onHide={() => setShowResults(false)} size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title>{t('pvp.results', 'Results')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <PvpQuizResultsModalContent data={quizResults} />
            </Modal.Body>
            <Modal.Footer className="justify-content-between flex-wrap gap-2">
              <Button variant="outline-secondary" onClick={() => navigate('/')}>
                {t('quiz.backToHome', 'Back to Home')}
              </Button>
              <Button variant="primary" onClick={() => navigate(HOME_PVP_ARENA_PATH)}>
                {t('pvp.asyncHostGoArena', 'Back to Arena')}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </>
    );
  }

  const isTileMatchQuiz = quiz?.format === 'TileMatch' || quiz?.format === 'tilematch';
  if (isTileMatchQuiz && tileMatchStart && asyncFlow && roomInfo) {
    const quizForTileMatch: Quiz = {
      id: quiz.id,
      title: quiz.title,
      titleEn: quiz.titleEn ?? null,
      theme: quiz.theme ?? null,
      themeEn: quiz.themeEn ?? null,
      format: 'TileMatch',
      difficulty: 'Medium',
      questionCount: 10,
    } as Quiz;

    return (
      <>
        <div className="quiz-page">
          <div className="quiz-container">
            {pvpParticipantsInlineCard}
            <TileMatchQuizPlayer
              quizId={quiz.id}
              quiz={quizForTileMatch}
              tileMatch={tileMatchStart}
              startTime={new Date(startTimeRef.current)}
              embeddedInPvp={true}
              onPvpQuitRequest={handleQuitQuiz}
              pvpAsync={{
                flow: asyncFlow,
                roomId: roomInfo.id,
                onComplete: handleTileMatchPvpComplete,
              }}
            />
          </div>
        </div>
        <Modal show={showQuitModal} onHide={() => setShowQuitModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{t('pvp.quitPvPQuiz', 'Quit PvP Quiz?')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t(
              'pvp.quitPvPQuizConfirm',
              'Are you sure you want to quit this PvP quiz? You will be removed from the room and your progress will be lost.'
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowQuitModal(false)}>
              {t('pvp.continuePvPQuiz', 'Continue Quiz')}
            </Button>
            <Button variant="danger" onClick={() => void confirmQuit()}>
              {t('pvp.quitPvPQuizButton', 'Quit Quiz')}
            </Button>
          </Modal.Footer>
        </Modal>
        <AsyncHostIndividualResultsModal
          show={showAsyncHostModal}
          onHide={() => {
            setShowAsyncHostModal(false);
            setAsyncHostQuizResponse(null);
            navigate(HOME_PVP_ARENA_PATH);
          }}
          roomName={roomInfo.name ?? ''}
          quizTitle={getQuizFullDisplayCaption(quizForTileMatch, i18n.language)}
          score={score}
          correctAnswers={correctAnswers}
          totalQuestions={10}
          earnedXpPreview={asyncHostEarnedXp}
          quizResponse={asyncHostQuizResponse}
          resultDurationSeconds={asyncHostDurationSeconds}
          publishBlockedDueToSkips={asyncHostPublishBlockedBySkips}
          opponentSkipsBlocked={asyncOpponentBlockedBySkips}
          inviteUrl={
            roomInfo.roomCode
            && !asyncHostPublishBlockedBySkips
            && !asyncOpponentBlockedBySkips
              ? `${getQuizterSiteUrl()}/pvp-quiz/${roomInfo.roomCode}`
              : undefined
          }
        />
        <QuizFinishCelebrationModal
          show={showCelebration}
          onFinished={handlePvpCelebrationFinished}
          achievements={celebrationAchievements}
          characters={celebrationCharacters}
          loginStreak={celebrationLoginStreak}
          playerNickname={celebrationPlayerNick}
        />
        <LevelUpCelebrationModal
          show={levelUpCelebration.showLevelUp}
          oldLevel={levelUpCelebration.levelUpOld}
          newLevel={levelUpCelebration.levelUpNew}
          onClose={handlePvpLevelUpFinished}
        />
        {showResults && quizResults && (
          <Modal show={showResults} onHide={() => setShowResults(false)} size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title>{t('pvp.results', 'Results')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <PvpQuizResultsModalContent data={quizResults} />
            </Modal.Body>
            <Modal.Footer className="justify-content-between flex-wrap gap-2">
              <Button variant="outline-secondary" onClick={() => navigate('/')}>
                {t('quiz.backToHome', 'Back to Home')}
              </Button>
              <Button variant="primary" onClick={() => navigate(HOME_PVP_ARENA_PATH)}>
                {t('pvp.asyncHostGoArena', 'Back to Arena')}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </>
    );
  }

  if (isWitcQuiz && witcStart && asyncFlow && roomInfo) {
    const quizForWitc: Quiz = {
      ...quiz,
      id: quiz.id,
      title: quiz.title,
      titleEn: quiz.titleEn ?? null,
      theme: quiz.theme ?? null,
      themeEn: quiz.themeEn ?? null,
      format: 'Witc',
      difficulty: roomInfo.quizDifficulty || quiz.difficulty || 'Medium',
      questionCount: quiz.questions.length,
      witcImageAssetsBasePath: quiz.witcImageAssetsBasePath ?? null,
    } as any as Quiz;

    const witcQuestions = quiz.questions.map((q) => ({
      id: q.id,
      quizId: quiz.id,
      text: q.text,
      textEn: q.textEn,
      imageAssetKey: q.imageAssetKey,
      options: [],
      correctAnswer: -1,
      explanation: '',
      witcMediumSkeletonPt: q.witcMediumSkeletonPt ?? null,
      witcMediumSkeletonEn: q.witcMediumSkeletonEn ?? null,
    }));

    return (
      <>
        <div className="quiz-page">
          <div className="quiz-container">
            {pvpParticipantsInlineCard}
            <WitcQuizPlayer
              quizId={quiz.id}
              quiz={quizForWitc}
              questions={witcQuestions as any}
              startTime={new Date(startTimeRef.current)}
              witcPlaySessionId={witcStart.playSessionId}
              witcEngineBootstrapCipher={witcStart.engineBootstrapCipher}
              playDifficulty={roomInfo.quizDifficulty}
              onPlayAgain={() => navigate(HOME_PVP_ARENA_PATH)}
              suppressResultsModal
              embeddedInPvp
              onPvpQuitRequest={handleQuitQuiz}
              pvpAsync={{
                flow: asyncFlow,
                roomId: roomInfo.id,
                onComplete: handleWitcPvpComplete,
              }}
            />
          </div>
        </div>
        <Modal show={showQuitModal} onHide={() => setShowQuitModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{t('pvp.quitPvPQuiz', 'Quit PvP Quiz?')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t(
              'pvp.quitPvPQuizConfirm',
              'Are you sure you want to quit this PvP quiz? You will be removed from the room and your progress will be lost.'
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowQuitModal(false)}>
              {t('pvp.continuePvPQuiz', 'Continue Quiz')}
            </Button>
            <Button variant="danger" onClick={() => void confirmQuit()}>
              {t('pvp.quitPvPQuizButton', 'Quit Quiz')}
            </Button>
          </Modal.Footer>
        </Modal>
        <AsyncHostIndividualResultsModal
          show={showAsyncHostModal}
          onHide={() => {
            setShowAsyncHostModal(false);
            setAsyncHostQuizResponse(null);
            navigate(HOME_PVP_ARENA_PATH);
          }}
          roomName={roomInfo.name ?? ''}
          quizTitle={getQuizFullDisplayCaption(quizForWitc, i18n.language)}
          score={score}
          correctAnswers={correctAnswers}
          totalQuestions={quiz.questions.length}
          earnedXpPreview={asyncHostEarnedXp}
          quizResponse={asyncHostQuizResponse}
          resultDurationSeconds={asyncHostDurationSeconds}
          publishBlockedDueToSkips={asyncHostPublishBlockedBySkips}
          opponentSkipsBlocked={asyncOpponentBlockedBySkips}
          inviteUrl={
            roomInfo.roomCode
            && !asyncHostPublishBlockedBySkips
            && !asyncOpponentBlockedBySkips
              ? `${getQuizterSiteUrl()}/pvp-quiz/${roomInfo.roomCode}`
              : undefined
          }
        />
        <QuizFinishCelebrationModal
          show={showCelebration}
          onFinished={handlePvpCelebrationFinished}
          achievements={celebrationAchievements}
          characters={celebrationCharacters}
          loginStreak={celebrationLoginStreak}
          playerNickname={celebrationPlayerNick}
        />
        <LevelUpCelebrationModal
          show={levelUpCelebration.showLevelUp}
          oldLevel={levelUpCelebration.levelUpOld}
          newLevel={levelUpCelebration.levelUpNew}
          onClose={handlePvpLevelUpFinished}
        />
        {showResults && quizResults && (
          <Modal show={showResults} onHide={() => setShowResults(false)} size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title>{t('pvp.results', 'Results')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <PvpQuizResultsModalContent data={quizResults} />
            </Modal.Body>
            <Modal.Footer className="justify-content-between flex-wrap gap-2">
              <Button variant="outline-secondary" onClick={() => navigate('/')}>
                {t('quiz.backToHome', 'Back to Home')}
              </Button>
              <Button variant="primary" onClick={() => navigate(HOME_PVP_ARENA_PATH)}>
                {t('pvp.asyncHostGoArena', 'Back to Arena')}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </>
    );
  }

  if (isMcQuiz && mcEngineBootstrapCipher && asyncFlow && roomInfo && quiz) {
    const playBandLabel = normalizePlayDifficultyLabel(roomInfo.quizDifficulty || quiz.difficulty);
    const maxPts = maxPointsForPlayDifficultyBand(playBandLabel);
    const quizForMc = {
      id: quiz.id,
      title: quiz.title,
      titleEn: quiz.titleEn ?? null,
      theme: quiz.theme ?? null,
      themeEn: quiz.themeEn ?? null,
      format: 'MultipleChoice',
      difficulty: playBandLabel,
      questionCount: quiz.questions.length,
      scorePerCorrectAnswer: maxPts,
      mcImageAssetsBasePath: quiz.mcImageAssetsBasePath ?? null,
      mcAnswerImageAssetsBasePath: quiz.mcAnswerImageAssetsBasePath ?? null,
      mcUseImageAnswers: quiz.mcUseImageAnswers ?? false,
    } as import('../services/api').Quiz;

    const mcQuestions = mapPvpMcQuestionsToApiQuestions(quiz.id, quiz.questions);

    return (
      <>
        <div className="quiz-page">
          <div className="quiz-container">
            {pvpParticipantsInlineCard}
            <McQuizPlayer
              quizId={quiz.id}
              quiz={quizForMc}
              questions={mcQuestions}
              startTime={new Date(startTimeRef.current)}
              mcPlaySessionId={mcPlaySessionId}
              mcEngineBootstrapCipher={mcEngineBootstrapCipher}
              playDifficulty={roomInfo.quizDifficulty}
              onPlayAgain={() => navigate(HOME_PVP_ARENA_PATH)}
              suppressResultsModal
              embeddedInPvp
              onPvpQuitRequest={handleQuitQuiz}
              pvpAsync={{
                flow: asyncFlow,
                roomId: roomInfo.id,
                onComplete: handleMcPvpComplete,
              }}
            />
          </div>
        </div>
        <Modal show={showQuitModal} onHide={() => setShowQuitModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{t('pvp.quitPvPQuiz', 'Quit PvP Quiz?')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t(
              'pvp.quitPvPQuizConfirm',
              'Are you sure you want to quit this PvP quiz? You will be removed from the room and your progress will be lost.'
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowQuitModal(false)}>
              {t('pvp.continuePvPQuiz', 'Continue Quiz')}
            </Button>
            <Button variant="danger" onClick={() => void confirmQuit()}>
              {t('pvp.quitPvPQuizButton', 'Quit Quiz')}
            </Button>
          </Modal.Footer>
        </Modal>
        <AsyncHostIndividualResultsModal
          show={showAsyncHostModal}
          onHide={() => {
            setShowAsyncHostModal(false);
            setAsyncHostQuizResponse(null);
            navigate(HOME_PVP_ARENA_PATH);
          }}
          roomName={roomInfo.name ?? ''}
          quizTitle={getQuizFullDisplayCaption(quizForMc, i18n.language)}
          score={score}
          correctAnswers={correctAnswers}
          totalQuestions={quiz.questions.length}
          earnedXpPreview={asyncHostEarnedXp}
          quizResponse={asyncHostQuizResponse}
          resultDurationSeconds={asyncHostDurationSeconds}
          publishBlockedDueToSkips={asyncHostPublishBlockedBySkips}
          opponentSkipsBlocked={asyncOpponentBlockedBySkips}
          inviteUrl={
            roomInfo.roomCode
            && !asyncHostPublishBlockedBySkips
            && !asyncOpponentBlockedBySkips
              ? `${getQuizterSiteUrl()}/pvp-quiz/${roomInfo.roomCode}`
              : undefined
          }
        />
        <QuizFinishCelebrationModal
          show={showCelebration}
          onFinished={handlePvpCelebrationFinished}
          achievements={celebrationAchievements}
          characters={celebrationCharacters}
          loginStreak={celebrationLoginStreak}
          playerNickname={celebrationPlayerNick}
        />
        <LevelUpCelebrationModal
          show={levelUpCelebration.showLevelUp}
          oldLevel={levelUpCelebration.levelUpOld}
          newLevel={levelUpCelebration.levelUpNew}
          onClose={handlePvpLevelUpFinished}
        />
        {showResults && quizResults && (
          <Modal show={showResults} onHide={() => setShowResults(false)} size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title>{t('pvp.results', 'Results')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <PvpQuizResultsModalContent data={quizResults} />
            </Modal.Body>
            <Modal.Footer className="justify-content-between flex-wrap gap-2">
              <Button variant="outline-secondary" onClick={() => navigate('/')}>
                {t('quiz.backToHome', 'Back to Home')}
              </Button>
              <Button variant="primary" onClick={() => navigate(HOME_PVP_ARENA_PATH)}>
                {t('pvp.asyncHostGoArena', 'Back to Arena')}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </>
    );
  }

  if (isStopQuiz && asyncFlow && roomInfo) {
    const quizForStop: Quiz = {
      id: quiz.id,
      title: quiz.title,
      titleEn: quiz.titleEn ?? null,
      theme: quiz.theme ?? null,
      themeEn: quiz.themeEn ?? null,
      format: 'Stop',
      difficulty: 'Medium',
      questionCount: quiz.questions.length,
      witcImageAssetsBasePath: quiz.witcImageAssetsBasePath,
    } as Quiz;

    const stopQuestions = quiz.questions.map((q) => ({
      id: q.id,
      text: q.text,
      textEn: q.textEn,
      imageAssetKey: q.imageAssetKey,
      options: [],
      correctAnswer: -1,
      explanation: '',
    }));

    return (
      <>
        <div className="quiz-page">
          <div className="quiz-container">
            {pvpParticipantsInlineCard}
            <StopQuizPlayer
              quizId={quiz.id}
              quiz={quizForStop}
              questions={stopQuestions as any}
              startTime={new Date(startTimeRef.current)}
              stopPlaySessionId={stopPlaySessionId}
              stopEngineBootstrapCipher={stopEngineBootstrapCipher}
              onPlayAgain={() => navigate(HOME_PVP_ARENA_PATH)}
              suppressResultsModal
              embeddedInPvp
              onPvpQuitRequest={handleQuitQuiz}
              pvpAsync={{
                flow: asyncFlow,
                roomId: roomInfo.id,
                onComplete: handleStopPvpComplete,
              }}
            />
          </div>
        </div>
        <Modal show={showQuitModal} onHide={() => setShowQuitModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{t('pvp.quitPvPQuiz', 'Quit PvP Quiz?')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t(
              'pvp.quitPvPQuizConfirm',
              'Are you sure you want to quit this PvP quiz? You will be removed from the room and your progress will be lost.'
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowQuitModal(false)}>
              {t('pvp.continuePvPQuiz', 'Continue Quiz')}
            </Button>
            <Button variant="danger" onClick={() => void confirmQuit()}>
              {t('pvp.quitPvPQuizButton', 'Quit Quiz')}
            </Button>
          </Modal.Footer>
        </Modal>
        <AsyncHostIndividualResultsModal
          show={showAsyncHostModal}
          onHide={() => {
            setShowAsyncHostModal(false);
            setAsyncHostQuizResponse(null);
            navigate(HOME_PVP_ARENA_PATH);
          }}
          roomName={roomInfo.name ?? ''}
          quizTitle={getQuizFullDisplayCaption(quizForStop, i18n.language)}
          score={score}
          correctAnswers={correctAnswers}
          totalQuestions={quiz.questions.length}
          earnedXpPreview={asyncHostEarnedXp}
          quizResponse={asyncHostQuizResponse}
          resultDurationSeconds={asyncHostDurationSeconds}
          publishBlockedDueToSkips={asyncHostPublishBlockedBySkips}
          opponentSkipsBlocked={asyncOpponentBlockedBySkips}
          inviteUrl={
            roomInfo.roomCode
            && !asyncHostPublishBlockedBySkips
            && !asyncOpponentBlockedBySkips
              ? `${getQuizterSiteUrl()}/pvp-quiz/${roomInfo.roomCode}`
              : undefined
          }
        />
        <QuizFinishCelebrationModal
          show={showCelebration}
          onFinished={handlePvpCelebrationFinished}
          achievements={celebrationAchievements}
          characters={celebrationCharacters}
          loginStreak={celebrationLoginStreak}
          playerNickname={celebrationPlayerNick}
        />
        <LevelUpCelebrationModal
          show={levelUpCelebration.showLevelUp}
          oldLevel={levelUpCelebration.levelUpOld}
          newLevel={levelUpCelebration.levelUpNew}
          onClose={handlePvpLevelUpFinished}
        />
        {showResults && quizResults && (
          <Modal show={showResults} onHide={() => setShowResults(false)} size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title>{t('pvp.results', 'Results')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <PvpQuizResultsModalContent data={quizResults} />
            </Modal.Body>
            <Modal.Footer className="justify-content-between flex-wrap gap-2">
              <Button variant="outline-secondary" onClick={() => navigate('/')}>
                {t('quiz.backToHome', 'Back to Home')}
              </Button>
              <Button variant="primary" onClick={() => navigate(HOME_PVP_ARENA_PATH)}>
                {t('pvp.asyncHostGoArena', 'Back to Arena')}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </>
    );
  }

  return (
    <>
      <div className="quiz-page">
        <div className="quiz-container">
          <Alert variant="warning">
            <Alert.Heading>{t('pvp.unsupportedFormat', 'Unsupported format')}</Alert.Heading>
            <p>
              {t(
                'pvp.unsupportedFormatOrNotReady',
                'This quiz cannot be played in the Arena yet. Return to the lobby and try again.'
              )}
            </p>
            <Button variant="outline-warning" onClick={() => navigate(HOME_PVP_ARENA_PATH)}>
              {t('pvp.asyncHostGoArena', 'Back to Arena')}
            </Button>
          </Alert>
        </div>
      </div>
    </>
  );
};

export default PvPQuiz;
